import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { signOut } from 'firebase/auth';
import { collection, getDocs, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, Package, Users, CalendarDays, Lock, Percent } from 'lucide-react';
import { useAdminRole } from '../hooks/UseAdminRole';
import AdminCalendar from '../components/AdminCalendar';
import PromotionManager from '../components/PromotionManager';
import { useAlert } from '../hooks/UseAlert';
import AlertModal from '../components/AlertModal';


const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState('planning');
  const navigate = useNavigate();
  const [paymentLoading, setPaymentLoading] = useState(null);

  const { success, error, warning, alertConfig, closeAlert } = useAlert();


  const { isFullAdmin, isRestrictedAdmin, loading: roleLoading } = useAdminRole();

  useEffect(() => {
    // Vérifier l'authentification
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      }
    });

    // Charger les données depuis localStorage
    loadData();

    return () => unsubscribe();
  }, [navigate]);

  const loadData = async () => {
    try {
      // Charger les réservations
      const bookingsRef = collection(db, 'bookings');
      const bookingsQuery = query(bookingsRef, orderBy('createdAt', 'desc'));
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsData = [];
      bookingsSnapshot.forEach((doc) => {
        bookingsData.push({ id: doc.id, ...doc.data() });
      });
      setBookings(bookingsData);

      // Charger les forfaits
      const packagesRef = collection(db, 'packages');
      const packagesQuery = query(packagesRef, orderBy('createdAt', 'desc'));
      const packagesSnapshot = await getDocs(packagesQuery);
      const packagesData = [];
      packagesSnapshot.forEach((doc) => {
        packagesData.push({ id: doc.id, ...doc.data() });
      });
      setPackages(packagesData);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };


  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleBackToSite = () => {
    navigate('/');
  };


  const handleCapturePayment = async (bookingId, paymentIntentId) => {
    if (!paymentIntentId) {
      error('Aucun PaymentIntent associé');
      return;
    }

    setPaymentLoading(bookingId);

    try {
      const response = await fetch('/api/capture-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentIntentId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur Stripe');
      }

      await updateDoc(doc(db, 'bookings', bookingId), {
        paymentStatus: 'captured'
      });

      success('Paiement encaissé avec succès');
      loadData();
    } catch (error) {
      console.error('Erreur capture:', error);
      error('Erreur lors de l’encaissement');
    } finally {
      setPaymentLoading(null);
    }
  };


  const handleRefundPayment = async (bookingId, paymentIntentId) => {
    if (!paymentIntentId) {
      error('Aucun PaymentIntent associé');
      return;
    }

    if (!window.confirm('Confirmer le remboursement ?')) return;

    setPaymentLoading(bookingId);

    try {
      const response = await fetch('/api/refund-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentIntentId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur Stripe');
      }

      await updateDoc(doc(db, 'bookings', bookingId), {
        paymentStatus: 'refunded',
        status: 'cancelled'
      });

      success('Remboursement effectué');
      loadData();
    } catch (error) {
      console.error('Erreur remboursement:', error);
      error('Erreur lors du remboursement');
    } finally {
      setPaymentLoading(null);
    }
  };


  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
        <header className="bg-white shadow-md">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-light text-purple-900">Administration</h1>
            <button
              onClick={handleBackToSite}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-purple-50 rounded-lg transition-colors"
            >
              <Package className="w-5 h-5" />
              Retour au site
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Réservations</p>
                  <p className="text-2xl font-semibold text-purple-900">{bookings.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Forfaits actifs</p>
                  <p className="text-2xl font-semibold text-purple-900">{packages.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm">Clients uniques</p>
                  <p className="text-2xl font-semibold text-purple-900">
                    {new Set([...bookings.map(b => b.userEmail), ...packages.map(p => p.clientEmail)]).size}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Onglets */}
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('planning')}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${activeTab === 'planning'
                    ? 'border-b-2 border-purple-600 text-purple-600'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <CalendarDays className="w-5 h-5" />
                Planning
              </button>
              <button
                onClick={() => setActiveTab('bookings')}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${activeTab === 'bookings'
                    ? 'border-b-2 border-purple-600 text-purple-600'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Calendar className="w-5 h-5" />
                Réservations
              </button>
              <button
                onClick={() => setActiveTab('packages')}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${activeTab === 'packages'
                    ? 'border-b-2 border-purple-600 text-purple-600'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Package className="w-5 h-5" />
                Collections
              </button>
              <button
                onClick={() => setActiveTab('promotions')}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${activeTab === 'promotions'
                    ? 'border-b-2 border-purple-600 text-purple-600'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <Percent className="w-5 h-5" />
                Promotions
              </button>
            </div>
          </div>

          {/* Contenu des onglets */}
          {activeTab === 'planning' && <AdminCalendar />}

          {activeTab === 'bookings' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-purple-900 mb-4">Liste des réservations</h2>

              {isRestrictedAdmin && (
                <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded">
                  <p className="text-sm text-amber-800">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Informations clients masquées en mode restreint
                  </p>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      {isFullAdmin && <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Client</th>}
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Heure</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Durée</th>
                      {isFullAdmin && (
                        <>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Soin</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Prix</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Type</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Paiement</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-gray-100 hover:bg-purple-50">
                        {isFullAdmin && (
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-900">{booking.userName}</p>
                              <p className="text-sm text-gray-500">{booking.userEmail}</p>
                            </div>
                          </td>
                        )}
                        <td className="py-3 px-4 text-gray-700">
                          {booking.date.split('-').reverse().join('/')}
                        </td>
                        <td className="py-3 px-4 text-gray-700">{booking.timeSlot}</td>
                        <td className="py-3 px-4 text-gray-700">{booking.serviceDuration}</td>
                        {isFullAdmin && (
                          <>
                            <td className="py-3 px-4 text-gray-700">{booking.serviceName}</td>
                            <td className="py-3 px-4 font-semibold text-purple-700">{booking.servicePrice}</td>
                          </>
                        )}
                        {isFullAdmin && (
                          <>
                            <td className="py-3 px-4 text-gray-700">{booking.serviceName}</td>
                            <td className="py-3 px-4 font-semibold text-purple-700">{booking.servicePrice}</td>
                            <td className="py-3 px-4">
                              {booking.paymentType === 'full' ? (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                  Complet
                                </span>
                              ) : booking.paymentType === 'deposit' ? (
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                                  Accompte
                                </span>
                              ) : (
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
                                  N/A
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 space-y-2">
                              {booking.paymentStatus === 'authorized' && (
                                <button
                                  onClick={() => handleCapturePayment(booking.id, booking.paymentIntentId)}
                                  disabled={paymentLoading === booking.id}
                                  className="w-full bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                                >
                                  💳 Encaisser
                                </button>
                              )}

                              {booking.paymentStatus === 'captured' && (
                                <>
                                  <span className="text-xs text-green-600 font-medium block">
                                    ✓ Encaissé
                                    {booking.paymentType === 'deposit' && (
                                      <span className="block text-gray-500">
                                        (Accompte {booking.amountPaid}€)
                                      </span>
                                    )}
                                    {booking.paymentType === 'full' && (
                                      <span className="block text-gray-500">
                                        (Total {booking.amountPaid}€)
                                      </span>
                                    )}
                                  </span>
                                  <button
                                    onClick={() => handleRefundPayment(booking.id, booking.paymentIntentId)}
                                    disabled={paymentLoading === booking.id}
                                    className="w-full bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:opacity-50"
                                  >
                                    ↩️ Rembourser
                                  </button>
                                </>
                              )}

                              {booking.paymentStatus === 'refunded' && (
                                <span className="text-xs text-gray-500 italic">
                                  Remboursé
                                </span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'packages' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-purple-900 mb-4">Forfaits actifs</h2>
              <div className="space-y-4">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-gray-900">{pkg.clientName}</h3>
                        <p className="text-sm text-gray-600">
                          {pkg.clientEmail} • {pkg.clientPhone}
                        </p>
                        <p className="text-purple-600 font-medium mt-2">{pkg.packageName}</p>
                        <p className="text-sm text-gray-600">
                          {pkg.sessions} séances • {pkg.discount} de réduction
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                          {pkg.remainingSessions} séances restantes
                        </span>
                        <p className="text-sm text-gray-500 mt-2">
                          Première séance : {new Date(pkg.firstSessionDate).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'promotions' && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <PromotionManager />
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default AdminDashboard;